import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateServiceCurrencyExchangeRate } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

export interface EditServiceCurrencyExchangeRateProps extends ModalProps {
    serviceID: number
    service: Service
}

interface FormValues {
    service_currency: string
    service_exchange_rate: number
}

const EditServiceCurrencyExchangeRate: React.FC<
    EditServiceCurrencyExchangeRateProps
> = ({ open, onClose, service, serviceID }) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const formControl = useForm<FormValues>({
        defaultValues: {
            service_currency: service?.service_currency || 'VND',
            service_exchange_rate: service?.service_exchange_rate || 1,
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateServiceCurrencyExchangeRate,
        },
        {
            successMessage: t('update-service-currency-exchange-rate-success'),
            errorMessage: t('error-update-service-currency-exchange-rate'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
            onSuccess: () => {
                refetch()
                onClose()
            },
        }
    )

    const handleSubmit = async (values: FormValues) => {
        mutateAsync({
            serviceID,
            service_currency: values.service_currency,
            service_exchange_rate: values.service_exchange_rate,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit-service-currency-exchange-rate')}
        >
            <GridHookForm
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        disabled: isPending,
                        variant: 'outlined',
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        disabled: isPending,
                        loading: isPending,
                        variant: 'contained',
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'service_currency',
                            type: 'select',
                            label: t('service-currency'),
                            options: [
                                { label: 'VND', value: 'VND' },
                                { label: 'USD', value: 'USD' },
                                { label: 'EUR', value: 'EUR' },
                                { label: 'JPY', value: 'JPY' },
                            ],
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'service_exchange_rate',
                            type: 'number',
                            label: t('service-exchange-rate'),
                        },
                        className: 'col-span-4',
                    },
                ]}
                formControl={formControl}
            />
        </Dialog>
    )
}

export default EditServiceCurrencyExchangeRate
